/*
 * The app's button (2026-10-18). Seventeen files each declared their own
 * `btnStyle`; new code uses this instead, and the old copies move over as
 * their files are touched. Colours are theme tokens only, so every theme
 * and both reading directions get it right. A coarse pointer's 44 px tap
 * target comes from index.css, which sizes every <button>.
 *
 *   variant  primary   the one thing the surface is for (Save, Send)
 *            secondary the way back out (Cancel, Close)
 *            danger    a destructive confirmation (Delete) -- only inside a
 *                      dialog that asked first, never beside the way out
 *            ghost     a quiet inline action
 *   busy     disables it and says so (aria-busy), keeping its width
 *
 * Every other prop (onClick, title, data-testid, disabled, ...) goes to the
 * <button> as it is. `type` defaults to "button", so a Button inside a form
 * never submits it by accident.
 */
import { forwardRef } from 'react';

const BASE = {
  borderRadius: 6,
  padding: '8px 16px',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.2,
  border: '1px solid transparent',
  cursor: 'pointer',
};

export const VARIANTS = Object.freeze({
  primary: { background: 'var(--c-primary)', color: 'var(--c-on-accent)' },
  secondary: { background: 'var(--c-surface)', color: 'var(--c-text-body-fg)', borderColor: 'var(--c-border-strong-2)' },
  danger: { background: 'var(--c-danger)', color: 'var(--c-on-accent)' },
  ghost: { background: 'transparent', color: 'var(--c-primary-fg)' },
});

/** The style a Button of this variant has: for the few places that must
 * style something that is not a <button> (a link that looks like one). */
export function buttonStyle(variant = 'secondary', { disabled = false } = {}) {
  return {
    ...BASE,
    ...(VARIANTS[variant] || VARIANTS.secondary),
    ...(disabled ? { opacity: 0.6, cursor: 'not-allowed' } : null),
  };
}

const Button = forwardRef(function Button(
  { variant = 'secondary', busy = false, disabled = false, type = 'button', style, children, ...rest },
  ref,
) {
  const off = disabled || busy;
  return (
    <button ref={ref} type={type} disabled={off} aria-busy={busy || undefined}
      data-variant={variant} style={{ ...buttonStyle(variant, { disabled: off }), ...style }} {...rest}>
      {children}
    </button>
  );
});

export default Button;
